import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  Eye,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Search,
  Settings,
  Clock,
  AlertTriangle,
  X,
  CalendarIcon,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { managementService, PreCadastro, Kit } from '@/services/management.service';
import { userService } from '@/services/user.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ExtendedPreCadastro extends PreCadastro {
  coordenadora_nome?: string;
  colaborador_nome?: string;
  indicador_nome?: string;
  indicador_id?: number;
  user_id?: number;
}

const PreCadastros = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Server-side pagination
  const [preCadastros, setPreCadastros] = useState<ExtendedPreCadastro[]>([]);
  const [totalPreCadastros, setTotalPreCadastros] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // BigNumbers por situação
  const [bigNumbers, setBigNumbers] = useState<{
    totalPendente: number;
    totalAnalisando: number;
    totalAprovadas: number;
    totalReprovadas: number;
    totalAguardandoAssinaturas: number;
    totalConcluidos: number;
  } | null>(null);

  // Filtros server-side
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filtros por coordenador e colaborador
  const [selectedCoordFilter, setSelectedCoordFilter] = useState<string>('');
  const [selectedCoabFilter, setSelectedCoabFilter] = useState<string>('');
  const [colaboradores, setColaboradores] = useState<any[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);

  // Modal de detalhes
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPreCadastro, setSelectedPreCadastro] = useState<ExtendedPreCadastro | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Modal de aprovação
  const [approveOpen, setApproveOpen] = useState(false);
  const [kits, setKits] = useState<Kit[]>([]);
  const [selectedKit, setSelectedKit] = useState<string>('');
  const [approvingPreCadastro, setApprovingPreCadastro] = useState(false);
  const [coordenadoras, setCoordanadoras] = useState<any[]>([]);
  const [selectedCoordenadora, setSelectedCoordenadora] = useState<string>('');

  // Modal de reprovação
  const [reproveOpen, setReproveOpen] = useState(false);
  const [reproveReason, setReproveReason] = useState('');
  const [reprovingPreCadastro, setReprovingPreCadastro] = useState(false);

  // Confirmação de ações
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: string;
    preCadastro: ExtendedPreCadastro;
  } | null>(null);

  // Carrega pré-cadastros com paginação server-side
  const loadPreCadastros = useCallback(async (
    page = currentPage,
    limit = pageSize,
    searchTerm = search,
    status = statusFilter,
    start = startDate,
    end = endDate,
    coordId = selectedCoordFilter,
    colab = selectedCoabFilter,
    currentUser = user,
  ) => {
    try {
      const params: any = { page, limit };
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      if (status) params.status = status;
      if (start && end) {
        params.dateStart = format(start, 'yyyy-MM-dd');
        params.dateEnd = format(end, 'yyyy-MM-dd') + ' 23:59:59';
      }
      if (coordId) params.coordenadoraId = Number(coordId);
      if (currentUser?.role !== 'admin' && currentUser?.id !== 1088) {
        params.coordenadoraId = currentUser?.id;
      }
      if (colab) params.colaborador = colab;

      const response = await managementService.getPreCadastrosPaginated(params);
      if (response && response.data) {
        setPreCadastros(response.data);
        setTotalPreCadastros(response.total);
        if (response.bigNumbers) {
          setBigNumbers(response.bigNumbers);
        }
        // Extrair colaboradores únicos para o dropdown
        const colabs = [...new Set(
          response.data
            .map((p: any) => p.colaborador_responsavel)
            .filter((name: any) => name && name !== '--')
        )] as string[];
        if (colabs.length > 0) {
          setColaboradores(prev => {
            const merged = [...new Set([...prev, ...colabs])];
            return merged;
          });
        }
        if (response.bigNumbers) {
          setBigNumbers(response.bigNumbers);
        }

      } else {
        setPreCadastros([]);
        setTotalPreCadastros(0);
      }
    } catch (error) {
      console.error('Erro ao carregar pré-cadastros:', error);
      toast.error('Erro ao carregar pré-cadastros');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, search, statusFilter, startDate, endDate, selectedCoordFilter, selectedCoabFilter, user]);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);

      if (userData.role !== 'admin' && userData.role !== 'coordenadora' && userData.id !== 1088) {
        toast.error('Acesso restrito');
        navigate('/home');
        return;
      }

      loadAllData();
      setIsLoading(true);
      loadPreCadastros(1, pageSize, '', '', undefined, undefined, '', '', userData);
    } else {
      navigate('/authentication/side-login');
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadPreCadastros();
    }
  }, [currentPage, pageSize]);

  const totalPages = Math.ceil(totalPreCadastros / pageSize);

  const loadAllData = async () => {
    try {
      await Promise.all([
        loadKits(),
        loadCoordanadoras(),
        loadColaboradores(),
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    }
  };

  const loadKits = async () => {
    try {
      const result = await managementService.getKits();
      setKits(result);
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
    }
  };

  const loadCoordanadoras = async () => {
    try {
      const result = await userService.getCoordenadoras();
      setCoordanadoras(result || []);
    } catch (error) {
      console.error('Erro ao carregar coordenadoras:', error);
    }
  };

  const loadColaboradores = async () => {
    try {
      const result = await userService.getColaboradores();
      setColaboradores(result || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  // Debounce search - triggers server call after 1.5s
  const triggerSearch = (termo: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!termo.trim()) {
      setCurrentPage(1);
      loadPreCadastros(1, pageSize, '', statusFilter, startDate, endDate, selectedCoordFilter, selectedCoabFilter, user);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      loadPreCadastros(1, pageSize, termo.trim(), statusFilter, startDate, endDate, selectedCoordFilter, selectedCoabFilter, user);
    }, 1500);
  };

  const aplicarFiltroPorPeriodo = () => {
    if (!startDate || !endDate) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    setCurrentPage(1);
    loadPreCadastros(1, pageSize, search, statusFilter, startDate, endDate, selectedCoordFilter, selectedCoabFilter, user);
  };

  const limparFiltros = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSearch('');
    setStatusFilter('');
    setSelectedCoordFilter('');
    setSelectedCoabFilter('');
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setCurrentPage(1);
    loadPreCadastros(1, pageSize, '', '', undefined, undefined, '', '', user);
  };

  const openDetails = async (preCadastro: ExtendedPreCadastro) => {
    window.open(`/v2/pre-cadastros/${preCadastro.id}`, '_blank');
  };

  const handleApprove = async () => {
    if (!selectedPreCadastro || !selectedKit || !selectedCoordenadora) {
      toast.error('Preencha todos os campos');
      return;
    }

    setApprovingPreCadastro(true);
    try {
      await managementService.approveAnalysisPreCadastro(
        selectedPreCadastro.id,
        selectedKit,
        Number(selectedCoordenadora)
      );

      toast.success('Pré-cadastro aprovado com sucesso');
      setApproveOpen(false);
      setDetailsOpen(false);
      setSelectedKit('');
      setSelectedCoordenadora('');
      loadPreCadastros();
    } catch (error: any) {
      console.error('Erro ao aprovar:', error);
      toast.error(error.message || 'Erro ao aprovar pré-cadastro');
    } finally {
      setApprovingPreCadastro(false);
    }
  };

  const handleReprove = async () => {
    if (!selectedPreCadastro || !reproveReason.trim()) {
      toast.error('Preencha o motivo da reprovação');
      return;
    }

    setReprovingPreCadastro(true);
    try {
      await managementService.reproveAnalysisPreCadastro(
        selectedPreCadastro.id,
        reproveReason
      );

      toast.success('Pré-cadastro reprovado');
      setReproveOpen(false);
      setDetailsOpen(false);
      setReproveReason('');
      loadPreCadastros();
    } catch (error: any) {
      console.error('Erro ao reprovar:', error);
      toast.error(error.message || 'Erro ao reprovar pré-cadastro');
    } finally {
      setReprovingPreCadastro(false);
    }
  };

  const handleExportCSV = () => {
    try {
      const headers = ['Nome', 'CPF', 'Telefone', 'Cidade/Estado', 'Profissão', 'Situação', 'Data'];
      const data = preCadastros.map((p) => [
        p.fullName,
        p.cpfDocument,
        p.phone || '-',
        p.cityState || '-',
        p.job || '-',
        p.situacao,
        format(new Date(p.createdAt), 'dd/MM/yyyy', { locale: ptBR }),
      ]);

      const csv = [headers, ...data].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `precadastros-${Date.now()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Arquivo exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  const formatCPF = (cpf: string) => {
    const clean = cpf.replace(/\D/g, '');
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const clean = phone.replace(/\D/g, '');
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getSituacaoBadgeColor = (situacao: string) => {
    const colors: Record<string, string> = {
      pendente: 'bg-yellow-100 text-yellow-800',
      analisando: 'bg-blue-100 text-blue-800',
      aprovado: 'bg-green-100 text-green-800',
      reprovado: 'bg-red-100 text-red-800',
      aguardando_assinaturas: 'bg-purple-100 text-purple-800',
      concluido: 'bg-green-100 text-green-800',
    };
    return colors[situacao] || 'bg-gray-100 text-gray-800';
  };

  const getSituacaoIcon = (situacao: string) => {
    switch (situacao) {
      case 'aprovado':
      case 'concluido':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'reprovado':
      case 'deletado':
        return <XCircle className="w-4 h-4" />;
      case 'analisando':
        return <Clock className="w-4 h-4" />;
      case 'pendente':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Pré-Cadastros</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie e analise os pré-cadastros de novas revendedoras
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button
                size="sm"
                onClick={() => navigate('/pre-cadastros/settings')}
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                Configurações
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Cards de Resumo por Situação */}
      {bigNumbers && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('pendente'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'pendente', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-600">{bigNumbers.totalPendente}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('analisando'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'analisando', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Analisando</p>
              <p className="text-2xl font-bold text-blue-600">{bigNumbers.totalAnalisando}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('aprovado'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'aprovado', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Aprovadas</p>
              <p className="text-2xl font-bold text-green-600">{bigNumbers.totalAprovadas}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('reprovado'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'reprovado', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Reprovadas</p>
              <p className="text-2xl font-bold text-red-600">{bigNumbers.totalReprovadas}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('aguardando_assinaturas'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'aguardando_assinaturas', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Aguard. Assinaturas</p>
              <p className="text-2xl font-bold text-purple-600">{bigNumbers.totalAguardandoAssinaturas}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter('concluido'); setCurrentPage(1); loadPreCadastros(1, pageSize, search, 'concluido', startDate, endDate, selectedCoordFilter, selectedCoabFilter, user); }}>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Concluídos</p>
              <p className="text-2xl font-bold text-emerald-600">{bigNumbers.totalConcluidos}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filtros</CardTitle>
            <div className={cn(
              "transition-transform duration-200",
              filtersExpanded ? "rotate-180" : "rotate-0"
            )}>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <div className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          filtersExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        )}>
          <CardContent className="space-y-4 pb-6">
            {/* Busca com debounce */}
            <div>
              <label className="text-sm font-medium mb-2 block">Buscar por nome, CPF, telefone ou email</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Digite para buscar..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    triggerSearch(e.target.value);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Coordenadora e Colaborador */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Coordenadora</label>
                <Select value={selectedCoordFilter || 'all'} onValueChange={(value) => {
                  const newVal = value === 'all' ? '' : value;
                  setSelectedCoordFilter(newVal);
                  setCurrentPage(1);
                  loadPreCadastros(1, pageSize, search, statusFilter, startDate, endDate, newVal, selectedCoabFilter, user);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as coordenadoras" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as coordenadoras</SelectItem>
                    {coordenadoras.map((coord: any) => (
                      <SelectItem key={coord.id} value={coord.id.toString()}>
                        {coord.nome_razao_social || coord.fullName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Colaborador</label>
                <Select value={selectedCoabFilter || 'all'} onValueChange={(value) => {
                  const newVal = value === 'all' ? '' : value;
                  setSelectedCoabFilter(newVal);
                  setCurrentPage(1);
                  loadPreCadastros(1, pageSize, search, statusFilter, startDate, endDate, selectedCoordFilter, newVal, user);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os colaboradores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os colaboradores</SelectItem>
                    {colaboradores.map((colaborador) => (
                      <SelectItem key={colaborador.codigo} value={colaborador.nome_razao_social}>
                        #{colaborador.codigo} - {colaborador.nome_razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filtro por período - Calendar Pickers como Garantias */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-2 block">Data Início</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-2 block">Data Fim</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex gap-2">
                <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate}>
                  Aplicar Período
                </Button>
                <Button onClick={limparFiltros} variant="outline">
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              </div>
            </div>
          </CardContent>
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Lista de Pré-Cadastros ({totalPreCadastros})</CardTitle>
              <CardDescription>
                Gerencie e analise os pré-cadastros de novas revendedoras
              </CardDescription>
            </div>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 por pág.</SelectItem>
                <SelectItem value="100">100 por pág.</SelectItem>
                <SelectItem value="150">150 por pág.</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-2">
          {preCadastros.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Nenhum pré-cadastro encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dados Pessoais</TableHead>
                    <TableHead className="min-w-56">Moradia & Profissão</TableHead>
                    <TableHead className="w-32">Coordenadora & Indicador</TableHead>
                    <TableHead className="w-32">Colaborador</TableHead>
                    <TableHead>
                      <Select value={statusFilter || 'all'} onValueChange={(value) => {
                        const newStatus = value === 'all' ? '' : value;
                        setStatusFilter(newStatus);
                        setCurrentPage(1);
                        loadPreCadastros(1, pageSize, search, newStatus, startDate, endDate, selectedCoordFilter, selectedCoabFilter, user);
                      }}>
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue placeholder="Situação" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="analisando">Analisando</SelectItem>
                          <SelectItem value="aprovado">Aprovado</SelectItem>
                          <SelectItem value="aguardando_assinaturas">Aguardando Assinaturas</SelectItem>
                          <SelectItem value="concluido">Concluído</SelectItem>
                          <SelectItem value="reprovado">Reprovado</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preCadastros.map((preCadastro) => (
                    <TableRow key={preCadastro.id}>
                      {/* Dados Pessoais */}
                      <TableCell className="text-sm">
                        <div style={{ lineHeight: '1.3' }}>
                          {preCadastro.user_id ? (
                            <a
                              href={`/v2/usuarios/${preCadastro.user_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer"
                            >
                              {preCadastro.fullName}
                            </a>
                          ) : (
                            <b>{preCadastro.fullName}</b>
                          )}
                          <br />
                          <span className="text-xs">{formatCPF(preCadastro.cpfDocument)} <sup>({preCadastro.cpfRestrictions ? 'Restrição' : 'S/ restrição'})</sup></span>
                          <br />
                          <span className="text-xs">{preCadastro.cityState || '-'} - {formatPhone(preCadastro.phone)}</span>
                        </div>
                      </TableCell>

                      {/* Moradia & Profissão */}
                      <TableCell className="text-xs min-w-56">
                        <div style={{ lineHeight: '1.5' }}>
                          <span>Moradia: {preCadastro.residence || '-'} <sup>({preCadastro.residenceTime || '-'})</sup></span>
                          <br />
                          <span>Emprego: {preCadastro.job || '-'}</span>
                          <br />
                          <span>{preCadastro.income || '-'}</span>
                        </div>
                      </TableCell>

                      {/* Coordenadora */}
                      <TableCell className="text-xs w-32">
                        <div style={{ lineHeight: '1.4' }}>
                          <span className="font-semibold" title={preCadastro.coordenadora_nome || '--'}>
                            {preCadastro.coordenadora_nome || '--'}
                          </span>
                          {preCadastro.indicador_nome && (
                            <>
                              <br />
                              <a
                                href={`/v2/usuarios/${preCadastro.indicador_id}`}
                                target='_blank'
                                className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                title={`Indicação: ${preCadastro.indicador_nome}`}
                              >
                                Indicador: #{preCadastro.indicatedCode} {preCadastro.indicador_nome}
                              </a>
                            </>
                          )}
                        </div>
                      </TableCell>

                      {/* Colaborador Responsável */}
                      <TableCell className="text-xs font-semibold w-32 truncate">
                        <span title={preCadastro.colaborador_responsavel || '--'}>
                          {preCadastro.colaborador_responsavel || '--'}
                        </span>
                      </TableCell>

                      {/* Situação */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getSituacaoIcon(preCadastro.situacao)}
                          <Badge className={getSituacaoBadgeColor(preCadastro.situacao)}>
                            {preCadastro.situacao.toUpperCase()}
                          </Badge>
                        </div>
                      </TableCell>

                      {/* Data */}
                      <TableCell className="text-sm">
                        {format(new Date(preCadastro.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>

                      {/* Ações */}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetails(preCadastro)}
                          className="gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação server-side */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-2 px-3">
              <span className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalPreCadastros)} de {totalPreCadastros} pré-cadastro(s)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <span className="flex items-center text-sm px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Pré-Cadastro</DialogTitle>
            <DialogDescription>
              {selectedPreCadastro?.fullName}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : selectedPreCadastro ? (
            <div className="space-y-6">
              {/* Informações Pessoais */}
              <div>
                <h3 className="font-semibold mb-4">Informações Pessoais</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Nome Completo</p>
                    <p className="font-medium">{selectedPreCadastro.fullName}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CPF</p>
                    <p className="font-medium">{formatCPF(selectedPreCadastro.cpfDocument)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Telefone</p>
                    <p className="font-medium">{formatPhone(selectedPreCadastro.phone)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedPreCadastro.email}</p>
                  </div>
                </div>
              </div>

              {/* Informações de Endereço e Profissão */}
              <div>
                <h3 className="font-semibold mb-4">Endereço e Profissão</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Cidade/Estado</p>
                    <p className="font-medium">{selectedPreCadastro.cityState || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Moradia</p>
                    <p className="font-medium">{selectedPreCadastro.residence || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Profissão</p>
                    <p className="font-medium">{selectedPreCadastro.job || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Renda Aproximada</p>
                    <p className="font-medium">{selectedPreCadastro.income || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="font-semibold mb-4">Status</h3>
                <div className="flex items-center gap-2 mb-4">
                  {getSituacaoIcon(selectedPreCadastro.situacao)}
                  <Badge className={getSituacaoBadgeColor(selectedPreCadastro.situacao)}>
                    {selectedPreCadastro.situacao}
                  </Badge>
                </div>
              </div>

              {/* Data */}
              <div className="text-sm text-muted-foreground border-t pt-4">
                Criado em {format(new Date(selectedPreCadastro.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
              </div>

              {/* Botões de Ação */}
              {(user?.role === 'admin' || selectedPreCadastro.situacao === 'pendente') && (
                <div className="flex gap-2 pt-4 border-t">
                  {['pendente', 'analisando'].includes(selectedPreCadastro.situacao) && (
                    <>
                      <Button
                        onClick={() => setApproveOpen(true)}
                        className="gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Aprovar
                      </Button>
                      <Button
                        onClick={() => setReproveOpen(true)}
                        variant="destructive"
                        className="gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Reprovar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Modal de Aprovação */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Pré-Cadastro</DialogTitle>
            <DialogDescription>
              Preencha os dados para aprovar o pré-cadastro
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="kit-select">Kit</Label>
              <Select value={selectedKit} onValueChange={setSelectedKit}>
                <SelectTrigger id="kit-select">
                  <SelectValue placeholder="Selecione um kit" />
                </SelectTrigger>
                <SelectContent>
                  {kits.map((kit) => (
                    <SelectItem key={kit.id} value={kit.id.toString()}>
                      {kit.name} - R$ {Number(kit.price).toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="coordenadora-select">Coordenadora</Label>
              <Select value={selectedCoordenadora} onValueChange={setSelectedCoordenadora}>
                <SelectTrigger id="coordenadora-select">
                  <SelectValue placeholder="Selecione uma coordenadora" />
                </SelectTrigger>
                <SelectContent>
                  {coordenadoras.map((coord: any) => (
                    <SelectItem key={coord.id} value={coord.id.toString()}>
                      {coord.nome_razao_social || coord.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleApprove}
                disabled={approvingPreCadastro}
              >
                {approvingPreCadastro && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirmar Aprovação
              </Button>
              <Button
                onClick={() => setApproveOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Reprovação */}
      <Dialog open={reproveOpen} onOpenChange={setReproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprovar Pré-Cadastro</DialogTitle>
            <DialogDescription>
              Informe o motivo da reprovação
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reprove-reason">Motivo da Reprovação</Label>
              <Textarea
                id="reprove-reason"
                placeholder="Descreva o motivo da reprovação..."
                value={reproveReason}
                onChange={(e) => setReproveReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleReprove}
                disabled={reprovingPreCadastro}
                variant="destructive"
              >
                {reprovingPreCadastro && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirmar Reprovação
              </Button>
              <Button
                onClick={() => setReproveOpen(false)}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreCadastros;
